import React, { useState } from "react";
import Input from "../../containers/InputForm";
import { setPreload } from "../../../server/CartsOpen";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import showAlert from "../../../server/ShowAlert";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { TimeBigYearMonthDate } from "../../containers/Date";
import ProductInSale from "./ProductInSale";
import ProductPageWithImg from "../productPage/ProductPageWithImg";
import { ArrowRight, Save } from "lucide-react";
import { updatePromotion } from "../../../server/PriceList";
import {
  formatDateTimeForJerusalem,
  getGtinsFromPromotionEntries,
  getProductCurrentPrice,
  getPromoId,
  getPromoStatusLabel,
  getPromoStatusSelectOptions,
  getPromoTypeLabel,
  getPromoValueLabel,
  getPromotionApiErrorMessage,
  isQuantityBasedPromoType,
  PROMO_TYPES,
  toDateInputValue,
  validatePromotionBeforeSave,
} from "./promotionUtils";
import "./Sale.scss";

const emptyToNull = (value, original) =>
  original === "" || original === null || original === undefined ? null : value;

const optionalInt = () =>
  yup
    .number()
    .nullable()
    .transform(emptyToNull)
    .min(0, "לא יכול להיות שלילי")
    .integer("חייב להיות מספר שלם")
    .nullable();

const buildSchema = (promoType) => {
  const valueLabel = getPromoValueLabel(promoType);
  const minQuantityField = isQuantityBasedPromoType(promoType)
    ? yup
        .number()
        .typeError("חובה להכניס כמות")
        .transform(emptyToNull)
        .required("חובה להכניס כמות")
        .moreThan(0, "חייב להיות מספר גדול מ 0")
    : yup
        .number()
        .nullable()
        .transform(emptyToNull)
        .min(0, "לא יכול להיות שלילי")
        .nullable();

  let discountValueField = yup
    .number()
    .typeError(`חובה להכניס ${valueLabel}`)
    .required(`חובה להכניס ${valueLabel}`)
    .min(0, "לא יכול להיות שלילי")
    .test(
      "maxDigits",
      "עד 2 ספרות אחרי הנקודה",
      (number) => number == null || Number.isInteger(number * 10 ** 2),
    );

  if (promoType === "PERCENT") {
    discountValueField = discountValueField.max(
      100,
      "אחוז הנחה לא יכול להיות מעל 100",
    );
  }

  return yup
    .object({
      status: yup.string().required("חובה לבחור סטטוס"),
      description: yup
        .string()
        .nullable()
        .max(50, "אורך מקסימלי 50")
        .transform((v) => v ?? ""),
      minQuantity: minQuantityField,
      startDate: yup.string().required("חובה להכניס מתאריך"),
      endDate: yup.string().required("חובה להכניס עד תאריך"),
      totalAllocatedQty: optionalInt(),
      maxPerPurchase: optionalInt(),
      maxPerPeriod: optionalInt(),
      discountValue: discountValueField,
    })
    .required();
};

const toFormNumber = (value) => (value != null && value !== "" ? value : "");

const mapSaleToFormValues = (saleData) => ({
  status: saleData?.status || "ACTIVE",
  description: saleData?.description || "",
  minQuantity: toFormNumber(saleData?.minQuantity),
  totalAllocatedQty: toFormNumber(saleData?.totalAllocatedQty),
  maxPerPurchase: toFormNumber(
    saleData?.maxPerPurchase === 0 ? "" : saleData?.maxPerPurchase,
  ),
  maxPerPeriod: toFormNumber(saleData?.maxPerPeriod),
  discountValue: toFormNumber(saleData?.discountValue),
  startDate:
    toDateInputValue(saleData?.startDate) || toDateInputValue(new Date()),
  endDate: toDateInputValue(saleData?.endDate) || toDateInputValue(new Date()),
});

const SalePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const saleData = location.state?.saleData;
  const product = location.state?.product;
  const dispatch = useDispatch();
  const { stationId, itemsShow } = useSelector((state) => ({
    stationId: state.user.stationId,
    itemsShow: state.displayProduct.itemsShow,
  }));
  const promoType = saleData?.promoType || "FLAT";
  const promoId = getPromoId(saleData);
  const discountLabel = getPromoValueLabel(promoType);
  const isPackagePromoType = promoType === PROMO_TYPES.PACKAGE_PRICE;
  const minQuantityLabel = isPackagePromoType
    ? "כמות במארז"
    : promoType === PROMO_TYPES.QUANTITY_PRICE
      ? "כמות"
      : "מינימום";

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, dirtyFields },
    reset,
  } = useForm({
    mode: "onSubmit",
    defaultValues: mapSaleToFormValues(saleData),
    resolver: yupResolver(buildSchema(promoType)),
  });

  const currentStatus = watch("status");

  const [sale, setSale] = useState(saleData);
  const [productPopUp, setProductPopUp] = useState(false);

  const checkedDates = (startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    if (new Date(startDate) > new Date(endDate)) {
      showAlert({
        msgTitle: "תאריך התחלה חייב להיות לפני תאריך סיום.",
        msgType: "error",
      });
      return false;
    }
    if (today > start) {
      showAlert({
        msgTitle: "לא ניתן להוסיף/לערוך מבצע שכבר התחיל.",
        msgType: "error",
      });
      return false;
    }
    return true;
  };

  const save = async (data) => {
    if (!promoId) {
      showAlert({ msgTitle: "מזהה מבצע חסר", msgType: "error" });
      return;
    }

    const startDate = formatDate(data.startDate);
    const endDate = formatDate(data.endDate);

    if (!startDate || !endDate) {
      showAlert({ msgTitle: "יש להזין תאריכים תקינים", msgType: "error" });
      return;
    }

    if (!checkedDates(startDate, endDate)) return;

    const formattedStartDate = formatDateTimeForJerusalem(data.startDate);
    const formattedEndDate = formatDateTimeForJerusalem(data.endDate, {
      endOfDay: true,
    });
    const targetGtins = getGtinsFromPromotionEntries([sale || saleData]);
    const storeProducts = Array.isArray(itemsShow)
      ? itemsShow
      : Array.isArray(itemsShow?.products)
        ? itemsShow.products
        : [];
    const productPriceByGtin = {};
    for (const gtin of targetGtins) {
      const gtinStr = String(gtin);
      const fromStore = storeProducts.find((p) => String(p?.gtin) === gtinStr);
      const fromMain =
        product && String(product.gtin) === gtinStr ? product : null;
      productPriceByGtin[gtinStr] = getProductCurrentPrice(
        fromStore || fromMain,
      );
    }

    const validationError = validatePromotionBeforeSave({
      branch: stationId,
      promoType,
      status: data.status,
      discountValue: Number(data.discountValue),
      minQuantity:
        data.minQuantity === "" || data.minQuantity == null
          ? null
          : Number(data.minQuantity),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      targetGtins,
      productPriceByGtin,
      existingPromotions: [],
      currentPromoId: promoId,
    });
    if (validationError) {
      showAlert({
        msgTitle: "לא ניתן לשמור את המבצע",
        msgText: validationError,
        msgType: "error",
      });
      return;
    }

    const payload = {
      description: data.description?.trim() || null,
      discountValue: Number(data.discountValue),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      minQuantity:
        data.minQuantity === "" || data.minQuantity == null
          ? null
          : Number(data.minQuantity),
      totalAllocatedQty:
        data.totalAllocatedQty === "" || data.totalAllocatedQty == null
          ? null
          : Number(data.totalAllocatedQty),
      maxPerPurchase:
        data.maxPerPurchase === "" || data.maxPerPurchase == null
          ? null
          : Number(data.maxPerPurchase),
      maxPerPeriod:
        data.maxPerPeriod === "" || data.maxPerPeriod == null
          ? null
          : Number(data.maxPerPeriod),
    };
    // Expired status is date-driven in APS — omit status so dates can recompute it.
    if (currentStatus !== "EXPIRED") {
      payload.status = data.status;
    }

    dispatch(setPreload(true));
    try {
      const updated = await updatePromotion(promoId, payload);
      const merged = { ...sale, ...updated, promoId };
      setSale(merged);
      reset(mapSaleToFormValues(merged));
      showAlert({ msgTitle: "המבצע נשמר בהצלחה", msgType: "success" });
      navigate("/admin/sales-management");
    } catch (error) {
      const message = getPromotionApiErrorMessage(error, "שגיאה בשמירת המבצע");
      showAlert({
        msgTitle: "לא ניתן לשמור את המבצע",
        msgText: message,
        msgType: "error",
      });
    } finally {
      dispatch(setPreload(false));
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d)) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  if (!saleData) {
    return (
      <div className="sale-detail-page">
        <p>לא נמצאו פרטי מבצע.</p>
        <NavLink to="/admin/sales-management">חזרה לרשימת המבצעים</NavLink>
      </div>
    );
  }

  return (
    <div className="sale-detail-page">
      {productPopUp && product && (
        <ProductPageWithImg
          selectedProd={product}
          close={() => setProductPopUp(false)}
        />
      )}

      <div className="sale-detail-page__topbar">
        <NavLink
          className="sale-detail-page__back"
          to={"/admin/sales-management"}
        >
          <ArrowRight aria-hidden size={16} strokeWidth={2.2} />
          חזרה לרשימת המבצעים
        </NavLink>
      </div>

      <h1 className="title-role">פרטי מבצע</h1>

      <div className="sale-detail-card">
        <form className="sale-form" onSubmit={handleSubmit(save)}>
          <div className="sale-form__grid">
            <span>
              <label className="p-title" htmlFor="status">
                סטטוס
              </label>
              {currentStatus === "EXPIRED" ? (
                <input
                  id="status"
                  type="text"
                  readOnly
                  value={getPromoStatusLabel(currentStatus)}
                  aria-readonly="true"
                />
              ) : (
                <select
                  id="status"
                  className={dirtyFields.status ? "dirtyFields" : ""}
                  {...register("status")}
                >
                  {getPromoStatusSelectOptions(currentStatus).map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={Boolean(option.disabled)}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
              {errors.status ? (
                <div className="text-danger">{errors.status.message}</div>
              ) : null}
            </span>

            <span>
              <label className="p-title">סוג מבצע</label>
              <input
                type="text"
                readOnly
                value={getPromoTypeLabel(promoType)}
                aria-readonly="true"
              />
            </span>

            <Input
              type="description"
              register={register}
              label={discountLabel}
              errors={errors}
              name="discountValue"
              dirtyFields={dirtyFields}
            />

            {!isQuantityBasedPromoType(promoType) ? (
              <Input
                type="description"
                register={register}
                label="תיאור מבצע (אופציונלי)"
                errors={errors}
                name="description"
                dirtyFields={dirtyFields}
              />
            ) : null}

            {/* readOnly, not disabled: react-hook-form drops disabled fields from
                the submitted payload, which would wipe the pack quantity. */}
            <Input
              type="number"
              register={register}
              label={minQuantityLabel}
              errors={errors}
              name="minQuantity"
              dirtyFields={dirtyFields}
              readOnly={isPackagePromoType}
            />

            <Input
              type="number"
              register={register}
              label="הקצאה"
              errors={errors}
              name="totalAllocatedQty"
              dirtyFields={dirtyFields}
            />

            <Input
              type="number"
              register={register}
              label="הגבלת קניה ללק׳"
              errors={errors}
              name="maxPerPurchase"
              dirtyFields={dirtyFields}
            />

            <Input
              type="number"
              register={register}
              label="הגבלת לק׳ לתקופה"
              errors={errors}
              name="maxPerPeriod"
              dirtyFields={dirtyFields}
            />

            <Input
              type="date"
              register={register}
              label="תאריך התחלה"
              errors={errors}
              name="startDate"
              dirtyFields={dirtyFields}
              defaultValue={TimeBigYearMonthDate(getValues("startDate"))}
            />

            <Input
              type="date"
              register={register}
              label="תאריך סיום"
              errors={errors}
              name="endDate"
              dirtyFields={dirtyFields}
              defaultValue={TimeBigYearMonthDate(getValues("endDate"))}
            />
          </div>

          <div className="sale-form__actions">
            <button className="sale-form__submit" type="submit">
              <Save aria-hidden size={16} strokeWidth={2.2} />
              שמור מבצע
            </button>
          </div>
        </form>
      </div>

      {sale ? (
        <div className="sale-panels">
          <ProductInSale sale={sale} checkedDates={checkedDates} />
        </div>
      ) : null}
    </div>
  );
};
export default SalePage;
